/*
  Wishlist — Swish (Wishlist King) bridge
  ---------------------------------------
  Every wishlist control in the theme is presentation-only: it announces a cancelable
  `wishlist:toggle` from the button and leaves the outcome to whoever is listening. This
  file is that listener. It cancels the event, calls the app, and owns the control's
  pressed state from there.

  The app embed publishes `window.WishlistKing`, loaded lazily, so this waits for it
  rather than assuming it. Its documented `swishApp` ES module is not what ships — the
  proxy serves a CommonJS bundle that throws on import, so the global is the only door in.

  `addWishlistItem` needs a product handle, so every control carries
  `data-product-handle` alongside `data-product-id`. Removing needs the saved row's own
  id, which only the app knows — hence the map from product id to row ids.

  The app is configured `wishlistMode: PRODUCT`, so a row is a product and no control
  answers per variant. `observeWishlist` is a live observable: it re-emits on every
  change, including changes made on the app's own wishlist page, and that emission is
  the single source of truth for every control's state and the header count.
*/
(function () {
  if (window.__wishlist) return;
  window.__wishlist = true;

  var CONTROL = '[data-wishlist]';
  var READY_TIMEOUT = 10000;

  function key(value) {
    return value == null ? '' : String(value);
  }

  /* ---------- The app ---------- */

  var ready;
  var warned = false;

  function king() {
    if (ready) return ready;
    ready = new Promise(function (resolve, reject) {
      if (window.WishlistKing) {
        resolve(window.WishlistKing);
        return;
      }
      // `appLoading: LAZY` — the embed attaches the global well after this file runs.
      var started = new Date().getTime();
      var timer = setInterval(function () {
        if (window.WishlistKing) {
          clearInterval(timer);
          resolve(window.WishlistKing);
        } else if (new Date().getTime() - started > READY_TIMEOUT) {
          clearInterval(timer);
          reject(new Error('WishlistKing never loaded — is the Swish app embed on for this theme?'));
        }
      }, 100);
    }).catch(function (error) {
      // Said once, because the rollback on its own looks identical to a dead button and
      // this is the only place the reason exists.
      if (!warned) {
        warned = true;
        console.warn('Wishlist: ' + error.message + ' Nothing will be saved.');
      }
      ready = null;
      throw error;
    });
    return ready;
  }

  /* ---------- Saved state ---------- */

  // Product id -> the row ids saved under it. Rows are products, not variants.
  var saved = Object.create(null);
  var listLoaded = false;

  function rowsFor(productId) {
    return saved[key(productId)] || [];
  }

  function isSaved(productId) {
    return rowsFor(productId).length > 0;
  }

  /* ---------- Controls ---------- */

  function press(el, on) {
    el.setAttribute('aria-pressed', on ? 'true' : 'false');
    var slot = el.querySelector('[data-pa-label]');
    var save = el.getAttribute('data-label-save');
    var savedLabel = el.getAttribute('data-label-saved');
    if (!slot || !save || !savedLabel) return;
    // Write into the typography span, not its wrapper — textContent would drop it.
    (slot.firstElementChild || slot).textContent = on ? savedLabel : save;
  }

  function syncAll() {
    if (!listLoaded) return;
    var nodes = document.querySelectorAll(CONTROL);
    for (var i = 0; i < nodes.length; i++) {
      var productId = nodes[i].getAttribute('data-product-id');
      if (productId) press(nodes[i], isSaved(productId));
    }
  }

  function publishCount(total) {
    var badges = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = total;
      badges[i].classList.toggle('is-empty', total === 0);
    }
    // The whole sentence, not the number: the attribute carries the translated format so
    // this file never has to build one.
    var texts = document.querySelectorAll('[data-wishlist-count-text]');
    for (var j = 0; j < texts.length; j++) {
      var text = texts[j].getAttribute('data-wishlist-count-text');
      if (text) texts[j].textContent = text.replace('%COUNT%', total);
    }
    // Same format, into the accessible name — an `aria-label` overrides hidden text inside.
    var labels = document.querySelectorAll('[data-wishlist-count-label]');
    for (var k = 0; k < labels.length; k++) {
      var label = labels[k].getAttribute('data-wishlist-count-label');
      if (label) labels[k].setAttribute('aria-label', label.replace('%COUNT%', total));
    }

    document.dispatchEvent(
      new CustomEvent('wishlist:updated', { detail: { count: total }, bubbles: true })
    );
  }

  /* ---------- Toggle ---------- */

  function add(app, handle, variantId) {
    if (!handle) return Promise.reject(new Error('Wishlist control has no product handle'));
    var input = { productHandle: handle };
    if (variantId) input.variantId = variantId;
    return Promise.resolve(app.addWishlistItem(input));
  }

  // One row per product in this mode, but the map is a list, so clear whatever is there.
  function remove(app, productId) {
    return rowsFor(productId).reduce(function (chain, itemId) {
      return chain.then(function () {
        return app.removeWishlistItem({ wishlistItemId: itemId });
      });
    }, Promise.resolve());
  }

  document.addEventListener('wishlist:toggle', function (e) {
    var el = e.target && e.target.closest ? e.target.closest(CONTROL) : null;
    if (!el) return;

    // Taking the event is what makes the control ours; the emitter stops driving it here.
    e.preventDefault();
    if (el.__wishlistBusy) return;

    var detail = e.detail || {};
    var handle = detail.handle || el.getAttribute('data-product-handle');
    var productId = detail.productId || el.getAttribute('data-product-id');
    var variantId = detail.variantId || el.getAttribute('data-variant-id') || '';
    var next =
      typeof detail.active === 'boolean' ? detail.active : el.getAttribute('aria-pressed') !== 'true';
    if (!productId) return;

    el.__wishlistBusy = true;
    press(el, next);

    king()
      .then(function (app) {
        return next ? add(app, handle, variantId) : remove(app, productId);
      })
      .then(function () {
        // `observeWishlist` re-emits and re-syncs every control from the new truth.
        el.__wishlistBusy = false;
      })
      .catch(function () {
        el.__wishlistBusy = false;
        press(el, !next);
      });
  });

  /* ---------- Lifecycle ---------- */

  // Quick add injects controls, and the fit guide repoints the ones it already rendered at
  // another product — both have to re-answer. One coalesced pass covers them.
  var queued = false;

  function scheduleSync() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      syncAll();
    });
  }

  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      if (records[i].type === 'attributes') {
        scheduleSync();
        return;
      }
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches(CONTROL) || node.querySelector(CONTROL)) {
          scheduleSync();
          return;
        }
      }
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-product-id', 'data-variant-id'],
  });

  king()
    .then(function (app) {
      // `subscribe(fn)`, not `subscribe({ next })` — the object form never emits.
      app.state.observeWishlist({ wishlistId: 'mine' }).subscribe(function (wishlist) {
        var items = (wishlist && wishlist.items) || [];
        saved = Object.create(null);
        for (var i = 0; i < items.length; i++) {
          var productId = items[i].product && items[i].product.id;
          if (productId == null) continue;
          var id = key(productId);
          if (!saved[id]) saved[id] = [];
          saved[id].push(items[i].id);
        }
        listLoaded = true;
        syncAll();
        publishCount(wishlist && wishlist.numItems != null ? wishlist.numItems : items.length);
      });
    })
    .catch(function () {});
})();
